import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, FormControl, Card, Dropdown } from "react-bootstrap";
import axios from "axios";
import apiConfig from "../credentials/apiConfig";
import IconDropdown from '../../images/icon-download.svg';
import './index.scss';

const FilterBlock = ({ onApply }) => {
    // State for filter options
    const [yearOptions, setYearOptions] = useState([]);
    const [industryOptions, setIndustryOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    // State for selected filters
    const [filters, setFilters] = useState({
        search: "",
        industry: "All Industries",
        yearEnd: [],
        status: "Status",
    });

    const [yearDropdownText, setYearDropdownText] = useState("Year End");

    useEffect(() => {
        const fetchIndustries = async () => {
            setLoading(true);
            try {
                const authToken = localStorage.getItem("authToken");
                const response = await axios.get(`${apiConfig.apiBaseURL}/getAllIndustries`, {
                    headers: {
                        "x-api-key": apiConfig["x-api-key"],
                        "Authorization": `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                });

                if (response.data && response.data.success && Array.isArray(response.data.data)) {
                    const industries = response.data.data.map((industry) => industry.name);
                    setIndustryOptions(industries);
                } else {
                    setIndustryOptions([]);
                }
            } catch (error) {
                console.error("Error fetching industries:", error);
                setIndustryOptions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchIndustries();

        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 5 }, (_, i) => {
            const year = (currentYear - i).toString();
            return { id: year, label: year, value: year };
        });
        setYearOptions(years);

        setLoading(false);
    }, []);

    const handleChange = (e) => {
        if (typeof e.target.value !== "string") {
            console.error(`Invalid value for ${e.target.name}:`, e.target.value);
            return;
        }

        const newFilters = { ...filters, [e.target.name]: e.target.value };
        setFilters(newFilters);


        if (e.target.name === "search") {
            onApply(newFilters);
        }
    };


    const handleYearToggle = (id) => {
        let updatedYearEnd = [...filters.yearEnd];

        if (updatedYearEnd.includes(id)) {
            // Remove if already selected
            updatedYearEnd = updatedYearEnd.filter(year => year !== id);
        } else {
            // Add if not selected
            updatedYearEnd.push(id);
        }

        setFilters({ ...filters, yearEnd: updatedYearEnd });

        // Update dropdown text based on selection
        if (updatedYearEnd.length === 0) {
            setYearDropdownText("Year End");
        } else if (updatedYearEnd.length === 1) {
            const selectedYear = yearOptions.find(year => year.id === updatedYearEnd[0]);
            setYearDropdownText(selectedYear ? selectedYear.label : "Year End");
        } else {
            setYearDropdownText(`${updatedYearEnd.length} Years Selected`);
        }
    };

    const handleApply = () => {
        onApply(filters);
    };

    const handleReset = () => {
        const resetFilters = {
            search: "",
            industry: "All Industries",
            yearEnd: [],
            status: "Status",
        };
        setFilters(resetFilters);
        setYearDropdownText("Year End");
        onApply(resetFilters);
    };

    return (
        <Form className="mb-3 credential-page filter-block" onSubmit={(e) => e.preventDefault()}>
            <Card className="py-3 px-2" style={{ border: '2px solid #ECEEF5' }}>
                <Card.Body>
                    <Row className="pb-1 position-relative">
                        {/* Search Input */}
                        <Col xl={3} lg={3} md="12" className="mb-3 mb-lg-0">
                            <FormControl
                                type="text"
                                name="search"
                                value={filters.search}
                                onChange={handleChange}
                                placeholder="Search"
                            />

                        </Col>

                        <Col xl="9" lg={9} md="12">
                            <Row className="justify-content-end">
                                <Col md="2" className="my-auto d-none d-lg-none d-xl-block">
                                    <p className="my-0 fw-bold text-dark" style={{ textAlign: 'right' }}>Filters</p>
                                </Col>
                                {/* Industry Dropdown */}
                                <Col xl="3" lg={4} className="mb-3 mb-lg-0">
                                    <Form.Select
                                        name="industry"
                                        value={filters.industry}
                                        onChange={handleChange}
                                        disabled={loading}

                                    >
                                        <option>All Industries</option>
                                        {industryOptions.map((industry, index) => (
                                            <option key={index} value={industry}>
                                                {industry}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                {/* Year End Multi-Select Dropdown */}
                                <Col lg="3" className="mb-3 mb-lg-0 custom-select">
                                    <Dropdown
                                        className="form-select"
                                        style={{ position: 'relative' }}
                                    >
                                        <Dropdown.Toggle
                                            variant="light"
                                            id="dropdown-year-end"
                                            className="w-100 text-start"
                                            style={{ color: "#212529" }}
                                        >
                                            {yearDropdownText}
                                            <img
                                                src={IconDropdown}
                                                alt='dropdown icon'
                                                style={{
                                                    position: 'absolute',
                                                    right: 10,
                                                    top: 14,
                                                    width: '14px'
                                                }}
                                            />
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="w-100">
                                            {yearOptions.map((option) => (
                                                <div key={option.id} className="px-2 py-1">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`year-${option.id}`}
                                                        label={option.label}
                                                        checked={filters.yearEnd.includes(option.id)}
                                                        onChange={() => handleYearToggle(option.id)}
                                                    />
                                                </div>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Col>

                                {/* Status Dropdown */}
                                <Col xl="2" lg={3} className="mb-3 mb-lg-0">
                                    <Form.Select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleChange}
                                    >
                                        <option>Status</option>
                                        <option value="open">Open</option>
                                        <option value="closed">Closed</option>
                                    </Form.Select>
                                </Col>

                                {/* Apply Button */}
                                <Col lg="2" className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        className="w-100"
                                        onClick={handleApply}
                                        disabled={loading}
                                    >
                                        Apply
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                        <div className="d-flex justify-content-end">
                            <p
                                className="my-0 fw-bold text-primary w-auto position-absolute font-14"
                                style={{ right: 12, cursor: 'pointer' }}
                                onClick={handleReset}
                            >
                                Reset Filters
                            </p>
                        </div>

                    </Row>
                </Card.Body>
            </Card>
        </Form>
    );
};

export default FilterBlock;
